/* eslint-disable react/prop-types */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import {useGetFormTemplatesQuery} from '../../../../api/services/formsService';
import CreateFormTemplatePreviewDialog from './CreateFormTemplatePreviewDialog';
import FormTemplatePreviewVisual, {getTemplateHeaderColour} from './FormTemplatePreviewVisual';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';

const ALL_CATEGORIES_VALUE = 'all-categories';
const ALL_SUB_CATEGORIES_VALUE = 'all-sub-categories';

const normaliseText = (value) => String(value || '').trim().toLowerCase();

const isInactiveCategoryValue = (value) => normaliseText(value) === 'inactive';

const getTemplateDisplayName = (template) => template?.name || template?.formName || '';

const getNormalisedTemplateMeta = (template) => ({
    category: normaliseText(template?.category),
    parentCategory: normaliseText(template?.parentCategory),
    type: normaliseText(template?.type),
});

const isBlankTemplate = (template) => getNormalisedTemplateMeta(template).category === 'blank';

const getTextColourForBackground = (hexColour) => {
    const hex = String(hexColour || '').replace('#', '');

    if (hex.length !== 6) return '#ffffff';

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

    return brightness > 160 ? '#1f2937' : '#ffffff';
};

const getTemplateCategoryNames = (template) => {
    const parentCategory = String(template?.parentCategory || '').trim();
    const category = String(template?.category || '').trim();

    if (!parentCategory) {
        return {
            category,
            subCategory: category,
        };
    }

    if (!category) {
        return {
            category: parentCategory,
            subCategory: parentCategory,
        };
    }

    if (
        normaliseText(parentCategory) === 'self service' &&
        normaliseText(category) === 'student admin'
    ) {
        return {
            category: 'Student Admin',
            subCategory: 'Student Admin',
        };
    }

    return {
        category: parentCategory,
        subCategory: category,
    };
};

const ClampedTemplateName = ({name}) => {
    const ref = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const checkOverflow = () => {
            const hasOverflow =
                el.scrollHeight > el.clientHeight + 1 ||
                el.scrollWidth > el.clientWidth + 1;

            setShowTooltip(hasOverflow);
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
        };
    }, [name]);

    return (
        <Tooltip
            title={showTooltip ? name : ''}
            disableHoverListener={!showTooltip}
        >
            <Typography
                ref={ref}
                variant="subtitle1"
                sx={{
                    fontWeight: 600,
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: 42,
                }}
            >
                {name}
            </Typography>
        </Tooltip>
    );
};

const CompactTemplateCard = ({
                                 template,
                                 previewEnabled,
                                 onPreview,
                                 onUse
                             }) => {
    const templateName = getTemplateDisplayName(template);
    const {
        category,
        subCategory
    } = getTemplateCategoryNames(template);

    return (
        <Card
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                },
            }}
        >
            <CardActionArea
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    flexGrow: 1,
                }}
                onClick={() => {
                    if (previewEnabled) {
                        onPreview(template);
                    } else {
                        onUse(template);
                    }
                }}
            >
                <FormTemplatePreviewVisual
                    category={template.category}
                    parentCategory={template.parentCategory}
                />
                <CardContent
                    sx={{
                        pt: 1.5,
                        pb: 1.5,
                        px: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flexGrow: 1,
                    }}
                >
                    <ClampedTemplateName name={templateName}/>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {category && !isInactiveCategoryValue(category) && (
                            <Chip
                                label={category}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {subCategory && subCategory !== category && !isInactiveCategoryValue(subCategory) && (
                            <Chip
                                label={subCategory}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                </CardContent>
            </CardActionArea>

            <Box p={1.5} pt={0} display="flex" gap={1}>
                {previewEnabled && (
                    <Button
                        variant="outlined"
                        onClick={() => onPreview(template)}
                        sx={{flex: 1}}
                    >
                        Preview
                    </Button>
                )}
                <Button
                    variant="contained"
                    onClick={() => onUse(template)}
                    sx={{
                        backgroundColor: '#186BBE',
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        },
                        boxShadow: 'none',
                        flex: previewEnabled ? 1 : '0 0 auto',
                        ml: previewEnabled ? 0 : 'auto',
                    }}
                >
                    Use
                </Button>
            </Box>
        </Card>
    );
};

const SubCategoryBlock = ({
                              subCategoryName,
                              templates,
                              previewEnabled,
                              onPreview,
                              onUse
                          }) => {
    return (
        <Box sx={{mb: 4}}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 2}}
            >
                <Typography variant="h6" sx={{fontWeight: 600}}>
                    {subCategoryName}
                </Typography>
                <Chip
                    size="small"
                    label={`${templates.length} ${templates.length === 1 ? 'template' : 'templates'}`}
                />
            </Stack>

            <Grid container spacing={2}>
                {templates.map((template) => (
                    <Grid key={template.id} item xs={12} sm={6} md={4} lg={3}>
                        <CompactTemplateCard
                            template={template}
                            previewEnabled={previewEnabled}
                            onPreview={onPreview}
                            onUse={onUse}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

const CreateFormTemplatePreviewList = ({
                                            onTemplateSelected,
                                            recipientAssignmentType,
                                            triggerRecipientAssignmentType,
                                            audience,
                                            linkToStudents,
                                            parentAccountType,
                                            formType,
                                            templateSource,
}) => {
    const {data: templates = []} = useGetFormTemplatesQuery();
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_VALUE);
    const [selectedSubCategory, setSelectedSubCategory] = useState(ALL_SUB_CATEGORIES_VALUE);
    const [expandedSection, setExpandedSection] = useState(null);
    const previewEnabled = false;
    const isSchoolSource = templateSource === 'school';
    const isTriggerForm = formType === 'trigger';

    const toValidAssignmentType = (v) => {
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    const recipientAssignmentTypeNum = toValidAssignmentType(recipientAssignmentType);
    const triggerRecipientAssignmentTypeNum = toValidAssignmentType(triggerRecipientAssignmentType);

    const effectiveRecipientAssignmentTypeNum =
        triggerRecipientAssignmentTypeNum ?? recipientAssignmentTypeNum ?? null;

    const recipientTypeAudience = (() => {
        if (audience !== FORM_AUDIENCE_TYPE.externalUsers) return audience;

        return FORM_AUDIENCE_TYPE.externalUsers;
    })();

    const filterBySource = (t) => isSchoolSource ? !!t.schoolId : !t.schoolId;

    const filterAdmicityTemplateByFormType = (t) => {
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const filterSchoolTemplateByFormType = (t) => {
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const isEmployeeTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        if (
            linkToStudents === true &&
            (
                parentCategory === 'staff' ||
                category === 'trips and activities'
            )
        ) {
            return false;
        }

        return (
            parentCategory === 'staff' ||
            category === 'staff admin' ||
            (category === 'trips' && parentCategory !== 'trips and activities') ||
            (!category && parentCategory === 'senior school admissions' && type === 'regular') ||
            (linkToStudents === true &&
                parentCategory === 'admissions' &&
                category === 'staff forms related to students' &&
                type === 'regular') ||
            (parentCategory === 'admissions' && [
                'boarding - staff',
                'sixth form - staff',
                'student admin - staff'
            ].includes(category))
        );
    };

    const isContactTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return !parentCategory && category === 'student admin' && type === 'contact';
    };

    const isParentTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            (!parentCategory && ['medical', 'clubs', 'sen and welfare', 'senior school admissions'].includes(category)) ||
            (!parentCategory && category === 'student admin' && type === 'contact') ||
            (!parentCategory && category === 'transport' && type === 'regular') ||
            (parentCategory === 'senior school admissions' && category === 'transport' && type === 'regular') ||
            (parentCategory === 'student admin' && ['self service', 'transport', 'school meals', 'meals'].includes(category)) ||
            (parentCategory === 'student admin' && !category && type === 'contact') ||
            (parentCategory === 'medical' && ['self service', 'sen'].includes(category)) ||
            (parentCategory === 'trips and activities' && ['clubs', 'trips'].includes(category)) ||
            (parentCategory === 'admissions' && category === 'new starters' && ['regular', 'contact'].includes(type)) ||
            (parentCategory === 'admissions' && ['sixth form', 'sixth form - students'].includes(category) && type === 'regular') ||
            (parentCategory === 'admissions' && category === 'admissions' && type === 'contact')
        );
    };

    const isParentBeforeAcceptanceTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            (!parentCategory && category === 'admissions' && ['regular', 'offer'].includes(type)) ||
            (parentCategory === 'admissions' && [
                'boarding',
                'independent schools',
                'admissions',
                'offers and refusals',
                'new starters',
                'sixth form - students'
            ].includes(category) && type !== 'transition') ||
            (parentCategory === 'admissions' && category === 'sixth form' && ['offer', 'regular'].includes(type))
        );
    };

    const isPreviousSchoolTemplate = (template) => {
        const {
            type,
            category
        } = getNormalisedTemplateMeta(template);

        return type === 'transition' && category !== 'blank';
    };

    const isExternalTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);
        const hasExternalKeyword =
            normaliseText(category).includes('external') ||
            normaliseText(parentCategory).includes('external');

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.employees) {
            return false;
        }

        if (
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students &&
            hasExternalKeyword
        ) {
            return true;
        }

        if (
            effectiveRecipientAssignmentTypeNum == null ||
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.none
        ) {
            return false;
        }

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students) {
            return (
                (!parentCategory && category === 'student admin' && type === 'contact') ||
                (parentCategory === 'student admin' && !category && type === 'contact') ||
                (parentCategory === 'admissions' && category === 'independent schools - external forms' && type === 'regular') ||
                (parentCategory === 'trips and activities' && category === 'clubs - external' && type === 'regular') ||
                hasExternalKeyword
            );
        }

        return false;
    };

    const isStudentTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            parentCategory === 'admissions' &&
            ['sixth form', 'sixth form - students'].includes(category) &&
            type === 'regular'
        );
    };

    const matchesTemplateAudience = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        const isParentApplicantStatusAudience =
            recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            ['without', 'applicant-status'].includes(parentAccountType);
        const isEnquiryAudience =
            recipientTypeAudience === FORM_AUDIENCE_TYPE.enquiries;
        const hasExternalKeyword =
            normaliseText(category).includes('external') ||
            normaliseText(parentCategory).includes('external');

        if (type === 'offer' && !isParentApplicantStatusAudience && !isEnquiryAudience) {
            return false;
        }

        if (
            recipientTypeAudience === FORM_AUDIENCE_TYPE.externalUsers &&
            type === 'transition'
        ) {
            return false;
        }

        if (
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students &&
            hasExternalKeyword
        ) {
            return true;
        }

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.employees) {
            return false;
        }

        if (formType === 'selfService' && recipientTypeAudience === FORM_AUDIENCE_TYPE.employees) {
            const topLevelCategory = normaliseText(getTemplateCategoryNames(template).category);
            return topLevelCategory === 'staff';
        }

        if (formType === 'selfService' && recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            const {
                category,
                subCategory
            } = getTemplateCategoryNames(template);
            const topLevelCategory = normaliseText(category);
            const childCategory = normaliseText(subCategory);
            const blockedSelfServiceParentCategories = [
                'admissions',
                'clubs',
                'senior school admissions',
            ];

            if (blockedSelfServiceParentCategories.includes(topLevelCategory)) {
                return false;
            }

            const isMedicalChildCategory = childCategory === 'medical';
            const isTopMedicalSelfService =
                topLevelCategory === 'medical' &&
                childCategory === 'self service';

            if (isMedicalChildCategory && !isTopMedicalSelfService) {
                return false;
            }
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.externalUsers) {
            return isExternalTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            if (['without', 'applicant-status'].includes(parentAccountType)) {
                return isParentBeforeAcceptanceTemplate(template) || isContactTemplate(template);
            }

            if (['with', 'with-account', 'without-account'].includes(parentAccountType)) {
                return isParentTemplate(template);
            }

            return isParentTemplate(template) || isParentBeforeAcceptanceTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.enquiries) {
            return isParentBeforeAcceptanceTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.employees) {
            return isEmployeeTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.previousSchool) {
            return isPreviousSchoolTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.students) {
            return isStudentTemplate(template);
        }

        return true;
    };

    const hasActiveCategoryData = (t) => {
        const {
            category,
            subCategory
        } = getTemplateCategoryNames(t);

        return (category || subCategory) &&
            !isInactiveCategoryValue(category) &&
            !isInactiveCategoryValue(subCategory);
    };

    const baseFilteredTemplates = useMemo(() => {
        return templates
            .filter(filterBySource)
            .filter(t => {
                // Admicity blank templates are no longer offered here - the wizard's
                // "Blank Form" creation method covers them. School templates saved from
                // a blank form keep the listing behaviour they already had.
                if (isBlankTemplate(t)) return isSchoolSource && isTriggerForm;

                if (isTriggerForm) return true;

                const passesFormType = isSchoolSource
                    ? filterSchoolTemplateByFormType(t)
                    : filterAdmicityTemplateByFormType(t);

                if (!passesFormType) return false;

                return matchesTemplateAudience(t);
            })
            .filter(hasActiveCategoryData);
    }, [
        templates,
        isTriggerForm,
        isSchoolSource,
        formType,
        audience,
        parentAccountType,
        recipientTypeAudience,
        effectiveRecipientAssignmentTypeNum
    ]);

    const categoryOptions = useMemo(() => {
        return Array.from(
            new Set(
                baseFilteredTemplates
                    .map(t => getTemplateCategoryNames(t).category)
                    .filter(Boolean)
                    .filter(category => !isInactiveCategoryValue(category))
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [baseFilteredTemplates]);

    const subCategoryOptions = useMemo(() => {
        let available = baseFilteredTemplates;

        if (selectedCategory !== ALL_CATEGORIES_VALUE) {
            available = available.filter(
                t => normaliseText(getTemplateCategoryNames(t).category) === normaliseText(selectedCategory)
            );
        }

        return Array.from(
            new Set(
                available
                    .map(t => getTemplateCategoryNames(t).subCategory)
                    .filter(Boolean)
                    .filter(subCategory => !isInactiveCategoryValue(subCategory))
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [baseFilteredTemplates, selectedCategory]);

    useEffect(() => {
        if (
            selectedCategory !== ALL_CATEGORIES_VALUE &&
            !categoryOptions.some(category => normaliseText(category) === normaliseText(selectedCategory))
        ) {
            setSelectedCategory(ALL_CATEGORIES_VALUE);
        }
    }, [categoryOptions, selectedCategory]);

    useEffect(() => {
        if (
            selectedSubCategory !== ALL_SUB_CATEGORIES_VALUE &&
            !subCategoryOptions.some(subCategory => normaliseText(subCategory) === normaliseText(selectedSubCategory))
        ) {
            setSelectedSubCategory(ALL_SUB_CATEGORIES_VALUE);
        }
    }, [subCategoryOptions, selectedSubCategory]);

    const searchedAndFilteredTemplates = useMemo(() => {
        const searchValue = normaliseText(searchTerm);

        return baseFilteredTemplates.filter((template) => {
            const {
                category,
                subCategory
            } = getTemplateCategoryNames(template);
            const templateName = getTemplateDisplayName(template);

            const matchesSearch = !searchValue ||
                normaliseText(templateName).includes(searchValue) ||
                normaliseText(subCategory).includes(searchValue) ||
                normaliseText(category).includes(searchValue) ||
                normaliseText(template.type).includes(searchValue);

            if (!matchesSearch) return false;

            const matchesSelectedCategory =
                selectedCategory === ALL_CATEGORIES_VALUE ||
                normaliseText(category) === normaliseText(selectedCategory);

            const matchesSelectedSubCategory =
                selectedSubCategory === ALL_SUB_CATEGORIES_VALUE ||
                normaliseText(subCategory) === normaliseText(selectedSubCategory);

            return matchesSelectedCategory && matchesSelectedSubCategory;
        });
    }, [baseFilteredTemplates, searchTerm, selectedCategory, selectedSubCategory]);

    const groupedTemplates = useMemo(() => {
        const groupsMap = new Map();

        searchedAndFilteredTemplates.forEach((template) => {
            const {
                category,
                subCategory
            } = getTemplateCategoryNames(template);

            if (!subCategory || isInactiveCategoryValue(subCategory) || isInactiveCategoryValue(category)) {
                return;
            }

            const sectionKey = category || subCategory;

            if (!groupsMap.has(sectionKey)) {
                groupsMap.set(sectionKey, {
                    categoryName: category,
                    subCategoriesMap: new Map(),
                });
            }

            const categoryGroup = groupsMap.get(sectionKey);

            if (!categoryGroup.subCategoriesMap.has(subCategory)) {
                categoryGroup.subCategoriesMap.set(subCategory, []);
            }

            categoryGroup.subCategoriesMap.get(subCategory).push(template);
        });

        return Array.from(groupsMap.values())
            .map((group) => {
                const subCategories = Array.from(group.subCategoriesMap.entries())
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([subCategoryName, templatesForSubCategory]) => ({
                        subCategoryName,
                        templates: templatesForSubCategory.sort((a, b) =>
                            getTemplateDisplayName(a).localeCompare(getTemplateDisplayName(b))
                        ),
                    }));

                const totalCount = subCategories.reduce((sum, subCategory) => sum + subCategory.templates.length, 0);

                return {
                    categoryName: group.categoryName || subCategories[0]?.subCategoryName || '',
                    subCategories,
                    totalCount,
                };
            })
            .filter(group => group.categoryName)
            .filter(group => !isInactiveCategoryValue(group.categoryName))
            .filter(group => group.subCategories.length > 0)
            .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    }, [searchedAndFilteredTemplates]);

    const accordionSections = useMemo(() => {
        return groupedTemplates.map((group) => ({
            key: `category-${group.categoryName}`,
            title: group.categoryName,
            totalCount: group.totalCount,
            subCategoryCount: group.subCategories.length,
            subCategories: group.subCategories,
        }));
    }, [groupedTemplates]);

    useEffect(() => {
        const firstSectionKey = accordionSections[0]?.key || null;

        setExpandedSection((current) => {
            if (!accordionSections.length) {
                return null;
            }

            if (current && accordionSections.some(section => section.key === current)) {
                return current;
            }

            return firstSectionKey;
        });
    }, [accordionSections]);

    const emptyMessage = isSchoolSource
        ? 'No school templates exist that match this form type, audience, or filter yet.'
        : 'No Admicity templates available for the current filter.';

    const handleSelectTemplate = (template) => {
        onTemplateSelected?.(template);
    };

    const handleAccordionChange = (sectionKey) => (event, isExpanded) => {
        setExpandedSection(isExpanded ? sectionKey : null);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            p: 2,
            bgcolor: '#fff'
        }}>
            <Stack direction="row" alignItems="center" justifyContent="center" mb={3}>
                <Typography variant="h4">
                    {isSchoolSource ? 'School Templates' : 'Admicity Templates'}
                </Typography>
            </Stack>

            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    backgroundColor: '#fff',
                    pb: 2,
                    mb: 3,
                    borderBottom: '1px solid #e0e0e0',
                }}
            >
                <Stack
                    direction={{
                        xs: 'column',
                        md: 'row'
                    }}
                    spacing={2}
                    sx={{
                        maxWidth: 1500,
                        width: '100%',
                        mx: 'auto',
                        pt: 1,
                    }}
                >
                    <TextField
                        fullWidth
                        label="Search templates"
                        placeholder="Search by template name, type, category, or sub-category"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />

                    <FormControl fullWidth sx={{minWidth: 220}}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            label="Category"
                            onChange={(event) => setSelectedCategory(event.target.value)}
                        >
                            <MenuItem value={ALL_CATEGORIES_VALUE}>All Categories</MenuItem>
                            {categoryOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{minWidth: 220}}>
                        <InputLabel>Sub-Category</InputLabel>
                        <Select
                            value={selectedSubCategory}
                            label="Sub-Category"
                            onChange={(event) => setSelectedSubCategory(event.target.value)}
                        >
                            <MenuItem value={ALL_SUB_CATEGORIES_VALUE}>All Sub-Categories</MenuItem>
                            {subCategoryOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            {searchedAndFilteredTemplates.length === 0
                ? (
                    <Typography color="text.secondary" textAlign="center" mt={6}>
                        {emptyMessage}
                    </Typography>
                )
                : (
                    <Box sx={{
                        maxWidth: 1500,
                        mx: 'auto',
                        width: '100%'
                    }}>
                        {accordionSections.map((section) => {
                            const isExpanded = expandedSection === section.key;
                            const sectionColour = getTemplateHeaderColour({
                                category: section.title,
                                parentCategory: section.title,
                            });
                            const sectionTextColour = getTextColourForBackground(sectionColour);

                            return (
                                <Accordion
                                    key={section.key}
                                    expanded={isExpanded}
                                    onChange={handleAccordionChange(section.key)}
                                    disableGutters
                                    sx={{
                                        mb: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '12px !important',
                                        overflow: 'hidden',
                                        boxShadow: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon/>}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            px: 2,
                                            py: 1,
                                            backgroundColor: sectionColour,
                                            color: sectionTextColour,
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'linear-gradient(135deg, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.12) 24%, rgba(255,255,255,0.04) 52%, rgba(255,255,255,0) 72%)',
                                                pointerEvents: 'none',
                                            },
                                            '& > .MuiAccordionSummary-content, & > .MuiAccordionSummary-expandIconWrapper': {
                                                position: 'relative',
                                                zIndex: 1,
                                            },
                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                                color: sectionTextColour,
                                            },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            width="100%"
                                            pr={1}
                                            spacing={2}
                                        >
                                            <Typography variant="h6" sx={{fontWeight: 700}}>
                                                {section.title}
                                            </Typography>

                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    size="small"
                                                    label={`${section.subCategoryCount} ${section.subCategoryCount === 1 ? 'sub-category' : 'sub-categories'}`}
                                                    sx={{
                                                        color: sectionTextColour,
                                                        borderColor: 'rgba(255,255,255,0.45)',
                                                        backgroundColor: sectionTextColour === '#ffffff'
                                                            ? 'rgba(255,255,255,0.16)'
                                                            : 'rgba(255,255,255,0.45)',
                                                    }}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={`${section.totalCount} ${section.totalCount === 1 ? 'template' : 'templates'}`}
                                                    sx={{
                                                        color: sectionTextColour,
                                                        borderColor: 'rgba(255,255,255,0.45)',
                                                        backgroundColor: sectionTextColour === '#ffffff'
                                                            ? 'rgba(255,255,255,0.16)'
                                                            : 'rgba(255,255,255,0.45)',
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{p: 2}}>
                                        {section.subCategories.map(({
                                                                        subCategoryName,
                                                                        templates
                                                                    }) => (
                                            <SubCategoryBlock
                                                key={`${section.key}-${subCategoryName}`}
                                                subCategoryName={subCategoryName}
                                                templates={templates}
                                                previewEnabled={previewEnabled}
                                                onPreview={setPreviewTemplate}
                                                onUse={handleSelectTemplate}
                                            />
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                )}

            {previewEnabled && (
                <CreateFormTemplatePreviewDialog
                    open={!!previewTemplate}
                    template={previewTemplate}
                    onClose={() => setPreviewTemplate(null)}
                    onUseTemplate={(template) => {
                        handleSelectTemplate(template);
                        setPreviewTemplate(null);
                    }}
                />
            )}
        </Box>
    );
};

export default CreateFormTemplatePreviewList;
